// NOTE: NSAI-6677 — the source-agnostic side of agent import. Every source
// (Manus, a JSON/CSV file, a future API) produces agents in this shape and gets
// one result row per agent back from the server.

export interface IAgentImportResult {
  agentId: string;
  success: boolean;
  assistantUuid?: string;
  name?: string;
  error?: string;
  // NOTE: Client-side only — set on rows whose batch request failed or was never
  // sent after an earlier batch had already succeeded, so the outcome is unknown.
  isOutcomeUnknown?: boolean;
}

// NOTE: One agent read out of an import file. `agentId` is a client-generated
// row id — the server only echoes it back on the matching result row.
export interface IAgentImportDraft {
  agentId: string;
  name: string;
  description: string | null;
  instructions: string | null;
}

export interface IImportAgentRequest {
  agentId: string;
  name: string;
  modelUuid: string;
  description?: string;
  instructions?: string;
}

export interface IImportAgentsPayload {
  agents: IImportAgentRequest[];
}

export enum EAgentImportFileError {
  UNSUPPORTED_FORMAT = "unsupportedFormat",
  TOO_LARGE = "tooLarge",
  INVALID_JSON = "invalidJson",
  UNEXPECTED_JSON_SHAPE = "unexpectedJsonShape",
  MISSING_NAME_COLUMN = "missingNameColumn",
  MALFORMED_CSV = "malformedCsv",
  NO_AGENTS = "noAgents",
  TOO_MANY_AGENTS = "tooManyAgents",
}

export enum EAgentImportRowError {
  NOT_AN_OBJECT = "notAnObject",
  MISSING_NAME = "missingName",
  NAME_TOO_LONG = "nameTooLong",
  DESCRIPTION_TOO_LONG = "descriptionTooLong",
  INSTRUCTIONS_TOO_LONG = "instructionsTooLong",
}

export interface IAgentImportRowIssue {
  // NOTE: 1-based position of the entry in the file — the CSV data row
  // (header excluded) or the JSON array index + 1 — so the UI can say
  // "entry 3" the way a person counts.
  position: number;
  reason: EAgentImportRowError;
}

export interface IParsedAgentImportFile {
  agents: IAgentImportDraft[];
  rowIssues: IAgentImportRowIssue[];
  // NOTE: Set when the file as a whole cannot be used; `agents` is then empty.
  fileError: EAgentImportFileError | null;
}

export interface IParseAgentImportFileProps {
  fileName: string;
  text: string;
}

// NOTE: NSAI-6593 — the selection state every agent import keeps (which agents
// are ticked and which model each gets), shared by web and mobile.
export interface IAgentSelection {
  isSelected: boolean;
  modelUuid: string;
}

export type TAgentSelectionMap = Record<string, IAgentSelection>;

export interface ISelectedAgent {
  agentId: string;
  modelUuid: string;
}

export interface IBuildAgentSelectionsProps {
  agentIds: string[];
  defaultModelUuid: string;
}

export interface IFillMissingAgentModelsProps {
  selections: TAgentSelectionMap;
  defaultModelUuid: string;
}

export interface IToggleAgentSelectionProps {
  selections: TAgentSelectionMap;
  agentId: string;
}

export interface IChangeAgentSelectionModelProps {
  selections: TAgentSelectionMap;
  agentId: string;
  modelUuid: string;
}
